import type { EvaluationKind, HeatmapCell, MatrixCell } from './types';
import type { ThresholdBand } from './heatmapUtils';
import { cellLevelText, cellStyleClass } from './heatmapUtils';

interface RiskHeatmapProps {
  cells: MatrixCell[] | HeatmapCell[];
  axisXLabel?: string;
  axisYLabel?: string;
  methodKind?: EvaluationKind;
  thresholds?: ThresholdBand[];
}

function isHeatmapCell(cell: MatrixCell | HeatmapCell): cell is HeatmapCell {
  return 'row' in cell && 'col' in cell;
}

function normalizeCells(cells: MatrixCell[] | HeatmapCell[]): HeatmapCell[] {
  if (cells.length === 0) return [];
  if (isHeatmapCell(cells[0])) return cells as HeatmapCell[];

  return (cells as MatrixCell[]).map((c) => ({
    row: c.probability,
    col: c.impact,
    count: c.count,
  }));
}

export function RiskHeatmap({
  cells,
  axisXLabel = 'Impacto',
  axisYLabel = 'Probabilidad',
  methodKind = 'prob_impacto',
  thresholds,
}: RiskHeatmapProps) {
  const normalized = normalizeCells(cells);
  const lookup = new Map<string, number>();
  for (const cell of normalized) {
    lookup.set(`${cell.row}-${cell.col}`, cell.count);
  }

  const axisLabels = [1, 2, 3, 4, 5];
  const rowLabels = [5, 4, 3, 2, 1];

  return (
    <div className='space-y-2' data-testid='risk-heatmap'>
      <div className='flex items-center gap-2 text-xs text-muted-foreground'>
        <span className='w-16 shrink-0 text-right'>{axisYLabel} ↑</span>
        <span className='flex-1 text-center'>{axisXLabel} →</span>
      </div>
      <div className='flex gap-2'>
        <div className='flex w-16 shrink-0 flex-col justify-around text-xs text-muted-foreground'>
          {rowLabels.map((row) => (
            <span key={row} className='text-right pr-1'>
              {row}
            </span>
          ))}
        </div>
        <div className='flex-1'>
          <div className='mb-1 grid grid-cols-5 gap-1 text-center text-xs text-muted-foreground'>
            {axisLabels.map((col) => (
              <span key={col}>{col}</span>
            ))}
          </div>
          <div className='grid grid-cols-5 gap-1'>
            {rowLabels.map((row) =>
              axisLabels.map((col) => {
                const count = lookup.get(`${row}-${col}`) ?? 0;
                const levelText = cellLevelText(row, col, methodKind, thresholds);
                const styleClass = cellStyleClass(row, col, methodKind, thresholds);
                return (
                  <div
                    key={`${row}-${col}`}
                    data-testid={`heatmap-cell-${row}-${col}`}
                    className={`flex min-h-[52px] flex-col items-center justify-center rounded border p-1 text-xs ${styleClass}`}
                    title={`${levelText} (${row}×${col})`}
                  >
                    <span className='font-semibold'>{count}</span>
                    <span className='text-[10px] opacity-80'>{levelText}</span>
                  </div>
                );
              }),
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
